import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  createAttribute,
  createAttributeOption,
  deleteAttributeOption,
  updateAttribute,
} from '../../services/productServices';
import { CrmButton, CrmDrawer, CrmInput, CrmTooltip } from '../ui';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import './catalog-drawers.scss';

const emptyOption = () => ({ id: null, value: '' });

function AddAttributeDialog({ open, onClose, attribute = null }) {
  const [attributeName, setAttributeName] = useState('');
  const [options, setOptions] = useState([emptyOption()]);
  const [originalOptionIds, setOriginalOptionIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!open) return;
    if (attribute?.id) {
      const existing = (attribute.options || []).map((option) => ({ id: option.id || null, value: String(option.value || '') }));
      setAttributeName(attribute.name || '');
      setOptions(existing.length ? existing : [emptyOption()]);
      setOriginalOptionIds(existing.map((option) => Number(option.id)).filter(Boolean));
    } else {
      setAttributeName('');
      setOptions([emptyOption()]);
      setOriginalOptionIds([]);
    }
  }, [attribute, open]);

  const close = () => {
    if (saving) return;
    onClose();
  };

  const save = async () => {
    const name = attributeName.trim();
    const validOptions = options.map((option) => ({ ...option, value: option.value.trim() })).filter((option) => option.value);
    if (!name) return setNotif({ open: true, message: 'Attribute name is required.', severity: 'warning' });
    if (!validOptions.length) return setNotif({ open: true, message: 'Add at least one attribute option.', severity: 'warning' });

    setSaving(true);
    try {
      let attributeId = attribute?.id;
      if (attributeId) {
        await updateAttribute(attributeId, { name });
        const keptIds = new Set(validOptions.map((option) => Number(option.id)).filter(Boolean));
        for (const optionId of originalOptionIds) {
          if (!keptIds.has(Number(optionId))) await deleteAttributeOption(optionId);
        }
      } else {
        const created = await createAttribute({ name });
        attributeId = created.attributeId;
      }

      for (const option of validOptions) {
        if (!option.id) await createAttributeOption({ attribute_id: attributeId, value: option.value });
      }

      setNotif({ open: true, message: attribute?.id ? 'Attribute updated successfully.' : 'Attribute created successfully.', severity: 'success' });
      setSaving(false);
      onClose();
    } catch (error) {
      setNotif({ open: true, message: error?.response?.data?.error || 'Failed to save attribute.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CrmDrawer
        open={open}
        onClose={close}
        title={attribute?.id ? 'Edit Attribute' : 'Add Attribute'}
        subtitle="Attributes describe products. Their options can later be selected as variation axes for variable products."
        size="md"
        footer={<CrmButton variant="primary" loading={saving} onClick={save}>{attribute?.id ? 'Update Attribute' : 'Save Attribute'}</CrmButton>}
      >
        <div className="crm-form-stack">
          <CrmInput
            required
            autoFocus
            label="Attribute Name"
            value={attributeName}
            onChange={(event) => setAttributeName(event.target.value)}
          />

          <div className="crm-form-section">
            <div className="crm-form-section__header">
              <div>
                <strong>Attribute Options</strong>
                <small>Add every value that can be assigned to this attribute.</small>
              </div>
              <CrmButton
                size="small"
                variant="secondary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => setOptions((previous) => [...previous, emptyOption()])}
              >
                Add Option
              </CrmButton>
            </div>

            <div className="crm-drawer-option-list">
              {options.map((option, index) => (
                <div className="crm-drawer-option-row" key={option.id || `new-${index}`}>
                  <CrmInput
                    label={`Option ${index + 1}`}
                    value={option.value}
                    onChange={(event) => setOptions((previous) => previous.map((item, itemIndex) => itemIndex === index ? { ...item, value: event.target.value } : item))}
                  />
                  <CrmTooltip content="Remove option">
                    <CrmButton
                      iconOnly
                      size="small"
                      variant="tertiary"
                      disabled={options.length === 1}
                      onClick={() => setOptions((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                      aria-label={`Remove option ${index + 1}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </CrmButton>
                  </CrmTooltip>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CrmDrawer>

      <NotificationSnackbar {...notif} onClose={() => setNotif((previous) => ({ ...previous, open: false }))} />
    </>
  );
}

export default AddAttributeDialog;
